import {
  Form,
  PrimaryButton,
  type SelectValue,
  SecondaryButton,
  Space,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { useGlobalCommandOrgCategoriesForm } from '../../hooks/useGlobalCommandOrgCategoriesForm'
import messages from '../../messages'
import {
  type IGlobalCommandOrgCategoriesFormPrimaryCategory,
  type IGlobalCommandOrgCategoriesFormSecondaryCategory,
} from '../../types'

export interface IGlobalCommandOrgCategoriesFormContentProps {
  primaryCategories: IGlobalCommandOrgCategoriesFormPrimaryCategory[]
  disabled?: boolean
  onCancel: () => void
}

export const GlobalCommandOrgCategoriesFormContent = ({
  primaryCategories,
  disabled,
  onCancel,
}: IGlobalCommandOrgCategoriesFormContentProps) => {
  const intl = useProcurifyIntl()

  const {
    primaryCategoryOptions,
    secondaryCategoryOptions,
    isSecondaryCategoryDisabled,
    isPrimaryCategoryOptionDisabled,
    handlePrimaryCategoryChange,
  } = useGlobalCommandOrgCategoriesForm({ primaryCategories })

  return (
    <Form.Content>
      <Space size='large' direction='vertical' align='stretch'>
        <Space size='medium' direction='vertical' align='stretch'>
          <Form.Select<
            SelectValue<IGlobalCommandOrgCategoriesFormPrimaryCategory>
          >
            id='GlobalCommandOrgCategoriesFormPrimaryCategory'
            name='primaryCategory'
            label={intl.formatMessage(messages.primaryCategoryLabel)}
            placeholder={intl.formatMessage(
              messages.primaryCategoryPlaceholder
            )}
            options={primaryCategoryOptions}
            isDisabled={disabled}
            isOptionDisabled={isPrimaryCategoryOptionDisabled}
            onChange={handlePrimaryCategoryChange}
            analyticsId='global-command-org-categories-form-primary-category'
            required
            fullWidth
          />

          <Form.Select<
            SelectValue<IGlobalCommandOrgCategoriesFormSecondaryCategory>
          >
            id='GlobalCommandOrgCategoriesFormSecondaryCategory'
            name='secondaryCategory'
            label={intl.formatMessage(messages.secondaryCategoryLabel)}
            placeholder={intl.formatMessage(
              messages.secondaryCategoryPlaceholder
            )}
            options={secondaryCategoryOptions}
            isDisabled={disabled || isSecondaryCategoryDisabled}
            analyticsId='global-command-org-categories-form-secondary-category'
            required
            fullWidth
          />
        </Space>

        <Space size='medium' direction='horizontal' align='stretch'>
          <Space.Item grow={1} flex>
            <SecondaryButton
              type='button'
              fullWidth
              disabled={disabled}
              onClick={onCancel}
              analyticsId='global-command-org-categories-form-cancel'
            >
              {intl.formatMessage(messages.cancelButtonText)}
            </SecondaryButton>
          </Space.Item>

          <Space.Item grow={1} flex>
            <PrimaryButton
              type='submit'
              fullWidth
              disabled={disabled}
              analyticsId='global-command-org-categories-form-save'
            >
              {intl.formatMessage(messages.saveButtonText)}
            </PrimaryButton>
          </Space.Item>
        </Space>
      </Space>
    </Form.Content>
  )
}
